// Bundled "starter" routines shown to every user — including brand-new and
// signed-out ones — so the app is never an empty page. They live in-repo (not
// the runtime store) so they survive a scorched-earth rebuild, and they're
// read-only: "Customize" seeds a fresh, editable copy rather than mutating the
// original (see useRoutines / RoutineBuilderPage).
import type { Routine } from '../types';

// A stable, fixed timestamp — starter routines have no real creation time and
// Date.now() would make them non-deterministic across renders/builds.
const EPOCH = '2026-01-01T00:00:00.000Z';

/**
 * Classic 12-position Sun Salutation (Surya Namaskar A, Hatha style): one full
 * cycle down to the floor and back up. The two Low Lunge steps are the right-
 * and left-leg halves of the flow, so each is pinned to a single side
 * (bilateral: false) rather than auto-running both sides.
 */
const SUN_SALUTATION: Routine = {
  id: 'builtin-sun-salutation',
  name: 'Sun Salutation',
  builtin: true,
  createdAt: EPOCH,
  steps: [
    { poseId: 'mountain-pose', holdSeconds: 12 }, // 1. Prayer Pose (Pranamasana)
    { poseId: 'upward-salute', holdSeconds: 12 }, // 2. Raised Arms (Hasta Uttanasana)
    { poseId: 'standing-forward-fold', holdSeconds: 12 }, // 3. Forward Fold (Uttanasana)
    { poseId: 'low-lunge', holdSeconds: 12, bilateral: false }, // 4. Low Lunge, right leg back
    { poseId: 'plank-pose', holdSeconds: 12 }, // 5. Plank (Kumbhakasana)
    { poseId: 'eight-limbed-pose', holdSeconds: 10 }, // 6. Knees-Chest-Chin (Ashtanga Namaskara)
    { poseId: 'cobra-pose', holdSeconds: 12 }, // 7. Cobra (Bhujangasana)
    { poseId: 'downward-dog', holdSeconds: 15 }, // 8. Downward Dog (Adho Mukha Svanasana)
    { poseId: 'low-lunge', holdSeconds: 12, bilateral: false }, // 9. Low Lunge, left leg back
    { poseId: 'standing-forward-fold', holdSeconds: 12 }, // 10. Forward Fold (Uttanasana)
    { poseId: 'upward-salute', holdSeconds: 12 }, // 11. Raised Arms (Hasta Uttanasana)
    { poseId: 'mountain-pose', holdSeconds: 12 }, // 12. Mountain / return (Tadasana)
  ],
};

export const STARTER_ROUTINES: Routine[] = [SUN_SALUTATION];

export const isBuiltinRoutine = (id: string): boolean =>
  STARTER_ROUTINES.some((r) => r.id === id);

export const getBuiltinRoutine = (id: string): Routine | undefined =>
  STARTER_ROUTINES.find((r) => r.id === id);
